import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Empty, Typography } from 'antd';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';

import { PlayerAnalysisType, selectors } from 'state';

const { Text } = Typography;

export type Props = {
  player: PlayerAnalysisType;
};

const CHART_HEIGHT_PX = 360;

function truncateLabel(s: string, max = 12) {
  if (s.length <= max) return s;
  return `${s.slice(0, max - 3)}...`;
}

const PlayerConcentrationTab: React.FC<Props> = ({ player }) => {
  const session = useSelector(selectors.activeSession);
  const repCcy = useSelector(
    selectors.currencyById(session.reporting_currency)
  );

  const concentrationData = useSelector(
    selectors.concentrationByPlayerId(player.player_id)
  ); // PlayerConcentrationType[] | []

  const { categories, values, total } = useMemo(() => {
    const rows = concentrationData ?? [];
    if (!rows.length)
      return { categories: [] as string[], values: [] as number[], total: 0 };

    const series = rows.map((item: any) => ({
      name: String(item.asset_name ?? ''),
      y: Math.round(item.reporting_value_period_weighted ?? 0),
    }));

    const cats = series.map(s => s.name);
    const vals = series.map(s => s.y);
    const totalVal = vals.reduce((acc, v) => acc + v, 0);

    return { categories: cats, values: vals, total: totalVal };
  }, [concentrationData]);

  const hasData = (concentrationData?.length ?? 0) > 0;

  const formattedTotal = useMemo(() => {
    // Avoid Intl currency crash if repCcy.symbol isn't an ISO currency code
    return `${Math.round(total).toLocaleString('en-US', {
      maximumFractionDigits: 0,
    })} ${repCcy.symbol}`;
  }, [total, repCcy.symbol]);

  const series = useMemo(() => [{ name: ' ', data: values }], [values]);

  const options = useMemo((): ApexOptions => {
    return {
      chart: {
        type: 'bar',
        height: CHART_HEIGHT_PX,
        toolbar: { show: false },
        animations: { enabled: false },
      },
      title: {
        text: 'Concentration (Period-weighted exposure)',
      },
      plotOptions: {
        bar: {
          horizontal: false, // column chart (vertical bars)
          distributed: true, // colorByPoint
          columnWidth: '55%',
        },
      },
      legend: { show: false },
      dataLabels: { enabled: false },
      xaxis: {
        categories: categories.map(c => truncateLabel(c, 18)),
        labels: {
          style: { fontSize: '12px' },
        },
        title: { text: undefined },
      },
      yaxis: {
        title: {
          text: `${repCcy.symbol} value | (Total: ${formattedTotal})`,
        },
        labels: {
          formatter: (v: number) =>
            Number.isFinite(v)
              ? Math.round(v).toLocaleString('en-US', {
                  maximumFractionDigits: 0,
                })
              : '',
        },
      },
      tooltip: {
        y: {
          formatter: (val: number) =>
            `${Math.round(val).toLocaleString('en-US', {
              maximumFractionDigits: 0,
            })} ${repCcy.symbol}`,
          title: { formatter: () => '' },
        },
      },
      grid: {
        padding: { left: 8, right: 8 },
      },
    };
  }, [categories, repCcy.symbol, formattedTotal]);

  if (!hasData) {
    return (
      <Empty
        description={
          <Text type="secondary">No concentration data available yet.</Text>
        }
      />
    );
  }

  return (
    <div style={{ width: '100%' }}>
      <Chart
        options={options}
        series={series as any}
        type="bar"
        height={CHART_HEIGHT_PX}
      />
    </div>
  );
};

export default PlayerConcentrationTab;
